// MARCA | 05/03/2026 | New file – Cart upsell custom element with Swiper carousel
import { Component } from "@theme/component";
import { ThemeEvents, CartAddEvent } from '@theme/events';
// MARCA | 27/03/2026 | Import recently viewed products and section renderer for recently viewed upsells
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

/** @type {string} Section ID used by the Section Rendering API for recently viewed product cards */
const RECENTLY_VIEWED_SECTION_ID = 'cart-upsell-recently-viewed';

/**
 * Custom element for the cart upsell carousel.
 * Initialises Swiper and handles add-to-cart for upsell products.
 * Supports two modes via `data-upsell-source`:
 *  - "metafields": server-rendered product cards (default)
 *  - "recently_viewed": client-loaded via Section Rendering API + localStorage
 *
 * @extends {Component}
 */
class CartUpsell extends Component {
  /** @type {any} Swiper instance (loaded from CDN) */
  #swiper = null;

  connectedCallback() {
    super.connectedCallback();

    // MARCA | 27/03/2026 | Branch initialisation based on upsell source
    if (this.#isRecentlyViewedMode) {
      this.#loadRecentlyViewedProducts();
    } else {
      this.#initSwiper();
      this.#bindAddButtons();
    }

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#destroySwiper();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  // MARCA | 27/03/2026 | Determine if the component is sourcing products from recently viewed
  get #isRecentlyViewedMode() {
    return this.dataset.upsellSource === "recently_viewed";
  }

  #destroySwiper() {
    if (this.#swiper) {
      this.#swiper.destroy(true, true);
      this.#swiper = null;
    }
  }

  #initSwiper() {
    const SwiperConstructor = /** @type {any} */ (globalThis).Swiper;
    if (!SwiperConstructor) return;

    const el = this.querySelector(".cart-upsell__swiper");
    if (!el) return;

    this.#swiper = new SwiperConstructor(el, {
      slidesPerView: 2.3,
      spaceBetween: 12,
      freeMode: true,
      watchOverflow: true,
    });
  }

  #bindAddButtons() {
    this.querySelectorAll(".cart-upsell__add-button").forEach((button) => {
      button.addEventListener("click", (e) => this.#handleAdd(e));
    });
  }

  /**
   * MARCA | 27/03/2026 | Fetch recently viewed product cards via the Section Rendering API.
   * Reads product IDs from localStorage, builds a search URL, and injects the
   * server-rendered slides into the Swiper carousel.
   * MARCA | 27/03/2026 | Swiper is kept alive during the fetch to prevent unstyled full-width cards flashing
   */
  async #loadRecentlyViewedProducts() {
    const viewedProductIds = RecentlyViewed.getProducts();
    if (viewedProductIds.length === 0) {
      this.#destroySwiper();
      this.hidden = true;
      return;
    }

    try {
      const url = new URL(Theme.routes.search_url, location.origin);
      url.searchParams.set(
        "q",
        viewedProductIds
          .map((/** @type {string} */ id) => `id:${id}`)
          .join(" OR "),
      );
      url.searchParams.set("type", "product");

      const html = await sectionRenderer.getSectionHTML(
        RECENTLY_VIEWED_SECTION_ID,
        false,
        url,
      );
      if (!html) {
        this.#destroySwiper();
        this.hidden = true;
        return;
      }

      const doc = new DOMParser().parseFromString(html, "text/html");
      const slides = doc.querySelectorAll(".swiper-slide");

      if (slides.length === 0) {
        this.#destroySwiper();
        this.hidden = true;
        return;
      }

      const swiperWrapper = this.querySelector(".swiper-wrapper");
      if (!swiperWrapper) return;

      // Destroy Swiper only once new slides are ready to swap in
      this.#destroySwiper();
      swiperWrapper.innerHTML = "";

      // Preserve localStorage ordering (most recently viewed first)
      const slidesByProductId = new Map();
      for (const slide of slides) {
        const card = slide.querySelector("[data-product-id]");
        if (card instanceof HTMLElement) {
          slidesByProductId.set(card.dataset.productId, slide);
        }
      }

      for (const productId of viewedProductIds) {
        const slide = slidesByProductId.get(productId);
        if (slide) swiperWrapper.appendChild(slide);
      }

      this.hidden = false;
      this.#initSwiper();
      this.#bindAddButtons();
    } catch (error) {
      console.error("Failed to load recently viewed products:", error);
      this.hidden = true;
    }
  }

  /**
   * @param {Event} event
   */
  async #handleAdd(event) {
    const button = /** @type {HTMLButtonElement} */ (event.currentTarget);
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.disabled = true;
    const textEl = button.querySelector(".cart-upsell__button-text");
    const originalText = textEl?.textContent || "";
    if (textEl) textEl.textContent = "Adding...";

    try {
      const shopify = /** @type {any} */ (window.Shopify);
      const routes = shopify?.routes?.root || "/";

      const response = await fetch(routes + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId, 10), quantity: 1 }],
        }),
      });

      if (!response.ok) throw new Error("Failed to add upsell product");

      await response.json();

      const cartResponse = await fetch(routes + "cart.js");
      const cartData = await cartResponse.json();

      document.dispatchEvent(
        new CartAddEvent(cartData, "cart-upsell", {
          itemCount: cartData.item_count,
          source: "cart-upsell",
        }),
      );

      if (textEl) textEl.textContent = "Added!";
      setTimeout(() => {
        button.disabled = false;
        if (textEl) textEl.textContent = originalText;
      }, 1500);
    } catch (error) {
      console.error("Failed to add upsell product:", error);
      button.disabled = false;
      if (textEl) textEl.textContent = originalText;
    }
  }

  #onCartUpdate = () => {
    requestAnimationFrame(() => {
      // MARCA | 27/03/2026 | Re-fetch recently viewed products on cart update to exclude newly added items
      if (this.#isRecentlyViewedMode) {
        this.#loadRecentlyViewedProducts();
        return;
      }

      if (this.#swiper) {
        this.#swiper.update();
      } else {
        this.#initSwiper();
      }
      this.#bindAddButtons();
    });
  };
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
